/*
 *
 * NotificationTemplateDetail
 *
 */

import React from "react"
import { connect } from "react-redux"
import {
	delayedDispatch,
	setBreadcrumb,
	setLoader,
	setTitle,
	updateCrumb,
} from "store/actions"
import { Col, Input, notification, Row, Select, Switch, Tabs } from "antd"
import { Helmet } from "react-helmet"
import { ContentWrapper } from "components"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import "./styles.scss"

class NotificationTemplateDetail extends React.Component<any, any> {
	constructor(props: any) {
		super(props)

		this.state = {
			notificationTemplate: null,
			hasUnsavedFields: false,
			activeLanguage: "pt",
			eventVariables: {},
			availableEvents: {},
		}

		this.handleSave = this.handleSave.bind(this)
	}

	async componentDidMount() {
		const { dispatch, match } = this.props
		const isNew = match?.params?.id === "new"

		dispatch(setTitle(""))

		if (!isNew) {
			await this.getData()
		} else {
			const listResponse = await API.get({
				url: Endpoints.uriNotificationTemplates(),
			})
			const { eventVariables = {}, availableEvents = {} } =
				listResponse.data?.results || {}
			this.setState({
				eventVariables,
				availableEvents,
				notificationTemplate: {
					name: "",
					active: true,
					event: "",
					title: { pt: "", en: "" },
					body: { pt: "", en: "" },
				},
			})
		}

		delayedDispatch(
			setBreadcrumb(() => {
				const { hasUnsavedFields, notificationTemplate } = this.state
				const isEditing = this.props.match?.params?.id !== "new"

				return {
					locations: [
						{
							text: Strings.sidebar.settings,
							route: "/settings",
							icon: "preferences",
						},
						{
							text: Strings.settings.notificationTemplates,
							route: "/settings/notification-templates",
							icon: "notification",
						},
						{
							text: isEditing
								? notificationTemplate?.name || "..."
								: Strings.generic.newM,
							icon: "notification",
						},
					],
					actions: [
						{
							type: "button",
							text: Strings.generic.save,
							onClick: this.handleSave,
							disabled: !hasUnsavedFields,
							className: hasUnsavedFields ? "BreadcrumbButtonSuccess" : "",
							isSave: true,
						},
					],
				}
			})
		)
	}

	componentDidUpdate() {
		const { dispatch } = this.props
		dispatch(updateCrumb())
	}

	async getData() {
		const { dispatch, match } = this.props

		dispatch(setLoader(true))

		try {
			const response = await API.get({
				url: Endpoints.uriNotificationTemplates(match?.params?.id),
			})

			if (response.ok) {
				const {
					notificationTemplates: notificationTemplate,
					eventVariables = {},
					availableEvents = {},
				} = response.data.results || {}
				this.setState({ notificationTemplate, eventVariables, availableEvents })
			} else {
				notification.error({
					message: Strings.settings.notificationTemplates,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.settings.notificationTemplates,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	async handleSave() {
		const { notificationTemplate } = this.state
		const { dispatch, match, user } = this.props
		const isNew = match?.params?.id === "new"

		if (!notificationTemplate?.name?.trim()) {
			return notification.error({
				message: Strings.settings.notificationTemplates,
				description: Strings.businesses.notificationTemplateNameRequired,
				placement: "bottomRight",
				duration: 5,
			})
		}

		const hasPtContent =
			notificationTemplate?.title?.pt?.trim() ||
			notificationTemplate?.body?.pt?.trim()

		if (!hasPtContent) {
			return notification.error({
				message: Strings.settings.notificationTemplates,
				description: Strings.businesses.notificationTemplateContentRequired,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(true))

		try {
			const request = isNew ? API.post : API.put
			const response = await request({
				url: Endpoints.uriNotificationTemplates(isNew ? "" : match?.params?.id),
				data: {
					name: notificationTemplate.name,
					event: notificationTemplate.event,
					active: notificationTemplate.active ?? true,
					title: notificationTemplate.title,
					body: notificationTemplate.body,
					lastModifiedBy: user?._id,
				},
			})

			if (response.ok) {
				this.setState({ hasUnsavedFields: false })

				notification.success({
					message: Strings.settings.notificationTemplates,
					description: isNew
						? Strings.settings.notificationTemplateAdded
						: Strings.settings.notificationTemplateEdited,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.settings.notificationTemplates,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.settings.notificationTemplates,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
	}

	insertVariable(field: "title" | "body", variable: string) {
		const { notificationTemplate, activeLanguage } = this.state
		const current = notificationTemplate?.[field]?.[activeLanguage] || ""
		this.setState((prev: any) => ({
			notificationTemplate: {
				...prev.notificationTemplate,
				[field]: {
					...prev.notificationTemplate[field],
					[activeLanguage]: current + `{{${variable}}}`,
				},
			},
			hasUnsavedFields: true,
		}))
	}

	renderVariables(field: "title" | "body") {
		const { notificationTemplate, eventVariables } = this.state
		const variables: string[] =
			eventVariables[notificationTemplate?.event] || []

		return (
			<div className="NotifVarChips">
				{variables.map((key: string) => (
					<button
						key={key}
						className="NotifVarChip"
						type="button"
						onClick={() => this.insertVariable(field, key)}
					>
						{key}
					</button>
				))}
			</div>
		)
	}

	renderLanguageTab(lang: string) {
		const { notificationTemplate } = this.state
		const titleValue = notificationTemplate?.title?.[lang] || ""
		const bodyValue = notificationTemplate?.body?.[lang] || ""

		return (
			<Row gutter={[12, 12]}>
				<Col xs={24}>
					<label className="InputLabel">
						{Strings.businesses.notificationTemplateTitle}
					</label>
					{this.renderVariables("title")}
					<Input
						placeholder={Strings.businesses.notificationTemplateTitle}
						value={titleValue}
						onChange={(e: any) => {
							const value = e.target.value
							this.setState((prev: any) => ({
								notificationTemplate: {
									...prev.notificationTemplate,
									title: { ...prev.notificationTemplate.title, [lang]: value },
								},
								hasUnsavedFields: true,
							}))
						}}
					/>
				</Col>
				<Col xs={24}>
					<label className="InputLabel">
						{Strings.businesses.notificationTemplateBody}
					</label>
					{this.renderVariables("body")}
					<Input.TextArea
						placeholder={Strings.businesses.notificationTemplateBody}
						value={bodyValue}
						autoSize={{ minRows: 4, maxRows: 8 }}
						onChange={(e: any) => {
							const value = e.target.value
							this.setState((prev: any) => ({
								notificationTemplate: {
									...prev.notificationTemplate,
									body: { ...prev.notificationTemplate.body, [lang]: value },
								},
								hasUnsavedFields: true,
							}))
						}}
					/>
				</Col>
			</Row>
		)
	}

	render() {
		const { notificationTemplate, activeLanguage, availableEvents } = this.state

		return (
			<ContentWrapper>
				<Helmet>
					<title>
						{notificationTemplate?.name ||
							Strings.settings.notificationTemplates}
					</title>
					<meta
						name="description"
						content={Strings.settings.notificationTemplatesDescription}
					/>
				</Helmet>
				<Row gutter={[12, 16]}>
					<Col xs={24}>
						<Row gutter={[16, 0]} align="middle">
							<Col flex="1">
								<label
									htmlFor="notif_name"
									className="InputLabel --label-required"
								>
									{Strings.fields.name}
								</label>
								<Input
									id="notif_name"
									placeholder={Strings.fields.name}
									value={notificationTemplate?.name || ""}
									onChange={(e: any) => {
										const value = e.target.value
										this.setState((prev: any) => ({
											notificationTemplate: {
												...prev.notificationTemplate,
												name: value,
											},
											hasUnsavedFields: true,
										}))
									}}
								/>
							</Col>
							<Col>
								<label className="InputLabel">
									{Strings.businesses.labelActive}
								</label>
								<div style={{ marginTop: 4 }}>
									<Switch
										checked={notificationTemplate?.active ?? true}
										onChange={(val: boolean) =>
											this.setState((prev: any) => ({
												notificationTemplate: {
													...prev.notificationTemplate,
													active: val,
												},
												hasUnsavedFields: true,
											}))
										}
									/>
								</div>
							</Col>
						</Row>
					</Col>

					<Col xs={24}>
						<label className="InputLabel --label-required">
							{Strings.settings.notificationEvent}
						</label>
						<Select
							style={{ width: "100%" }}
							placeholder={Strings.settings.notificationEvent}
							value={notificationTemplate?.event || undefined}
							onChange={(val: string) =>
								this.setState((prev: any) => ({
									notificationTemplate: {
										...prev.notificationTemplate,
										event: val,
									},
									hasUnsavedFields: true,
								}))
							}
						>
							{Object.entries(availableEvents).map(
								([key, config]: [string, any]) => (
									<Select.Option key={key} value={key}>
										{config.label?.[this.props.language || "pt"] || key}
									</Select.Option>
								)
							)}
						</Select>
					</Col>

					<Col xs={24}>
						<Tabs
							activeKey={activeLanguage}
							onChange={(key: string) => this.setState({ activeLanguage: key })}
						>
							<Tabs.TabPane tab="PT" key="pt">
								{this.renderLanguageTab("pt")}
							</Tabs.TabPane>
							<Tabs.TabPane tab="EN" key="en">
								{this.renderLanguageTab("en")}
							</Tabs.TabPane>
						</Tabs>
					</Col>
				</Row>
			</ContentWrapper>
		)
	}
}

const mapStateToProps = (state: any) => ({
	user: state.user,
})

export default connect(mapStateToProps)(NotificationTemplateDetail)
